import { getScrollBarStyles } from '@toqio/toqio-web-components';
import { createGlobalStyle, DefaultTheme } from 'styled-components';

import { getLargeSpacingClasses, getSmallSpacingClasses } from './spacingGlobal';
import getFlowableStyles from './flowableStyles';

const getViewportStyles = (): string => {
  return `
    // Make viewport responsive
    //
    // @viewport is needed because IE 10+ doesn't honor <meta name="viewport"> in
    // some cases. See http://timkadlec.com/2012/10/ie10-snap-mode-and-responsive-design/.
    // Eventually @viewport will replace <meta name="viewport">. It's been manually
    // prefixed for forward-compatibility.
    //
    // However, "device - width" is broken on IE 10 on Windows (Phone) 8,
    // (see http://timkadlec.com/2013/01/windows-phone-8-and-device-width/ and https://github.com/twbs/bootstrap/issues/10497)
    // and the fix for that involves a snippet of JavaScript to sniff the user agent
    // and apply some conditional CSS.
    //
    // See http://getbootstrap.com/getting-started/#support-ie10-width for the relevant hack.
    @-moz-viewport {
      width: device-width;
    }
    @-ms-viewport {
      width: device-width;
    }
    @-webkit-viewport {
      width: device-width;
    }
    @viewport {
      width: device-width;
    }
  `;
};

const getGeneralStyles = ({ theme }: { theme: DefaultTheme }): string => {
  return `
    *,
    *:before,
    *:after {
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased !important;
      outline: none;  
    }

    //
    // Reset HTML, body, and more
    //
    html {
      // Custom reset (borrowed from Bootstrap 4 ^.^)
      box-sizing: border-box;
      // Sets a specific default "font - size" for user with "rem" type scales.
      height: 100%;
      // Changes the default tap highlight to be completely transparent in iOS.
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      font-size: 14px;
      @media ${theme.media_queries.mediaM} {
        font-size: 16px;
      }
    }

    html * {
     font-family: ${theme.typography.fontFamily}, sans-serif;
    }
    
    body {
      height: 100vh;
      margin: 0;
      padding: 0;
      font-weight: ${theme.typography.fontWeight};
      font-family: ${theme.typography.fontFamily}, sans-serif;
      background: ${theme.color.general.background};
      color: ${theme.color.general.font.primary};
      box-sizing: border-box;
      font-size: 14px;
      @media ${theme.media_queries.mediaM} {
        font-size: 16px;
      }
    }
    
    p {
      margin-block-start: 0em;
    }

    a {
      text-decoration: none; /* no underline */
    }

    b {
      font-weight: bold;
    }
    
    #root {
      height: 100%;
      margin: 0;
      width: 100%;
      display: flex;
    }
  
    //
    // Temporary fix for the overflow issue on the workflow scroll bar issue
    //
    #root, body, html {
      overflow: visible;
    }
    
    .display-flex {
      display: flex;
      flex-direction: row;
      flex-basis: auto;
    }
    
    // layout
    // display column for resolution under SM
    .display-flex.column-lt-sm { 
      flex-direction: column;
      @media ${theme.media_queries.mediaS} {
        flex-direction: row;
      }
    }
    
    // display column for resolution under M
    .display-flex.column-lt-m { 
      flex-direction: column;
      @media ${theme.media_queries.mediaM} {
        flex-direction: row;
      }
    }
    
    // display column reverse for resolution under M  
    .display-flex.column-reverse-lt-m { 
      flex-direction: column-reverse;
      @media ${theme.media_queries.mediaM} {
        flex-direction: row;
      }
    }
    
    // display column reverse for resolution under L
    .display-flex.column-reverse-lt-lg {
      flex-direction: column-reverse;
      @media ${theme.media_queries.mediaL} {
        flex-direction: row;
      }
    }
    
    // display column for resolution under L
    .display-flex.column-lt-lg {
      flex-direction: column;
      @media ${theme.media_queries.mediaL} {
        flex-direction: row;
      }
    }
    
    // display column for resolution underXL
    .display-flex.column-lt-xl {
      flex-direction: column;
      @media ${theme.media_queries.mediaXL} {
        flex-direction: row;
      }
    }

    .display-flex.column {
      flex-direction: column;
    }

    .display-flex.center-x {
      justify-content: center;
    }

    .display-flex.center-y {
      align-items: center;
    }

    .display-flex.start-x {
      justify-content: flex-start;
    }

    .display-flex.start-y {
      align-items: flex-start;
    }

    .display-flex.end-x {
      justify-content: flex-end;
    }

    .display-flex.end-y {
      align-items: flex-end;
    }

    .display-flex.baseline-y {
      align-items: baseline;
    }

    .display-flex.strech-y {
      align-items: strech;
    }

    .display-flex.space-between-x {
      justify-content: space-between;
    }

    .display-flex.space-around-x {
      justify-content: space-around;
    }

    .display-flex.flex-wrap {
      flex-wrap: wrap;
    }

    .display-flex.flex-no-wrap {
      flex-wrap: nowrap;
    }

    .flex {
      flex: 1;
    }
  `;
};

const getContentStyles = ({ theme }: { theme: DefaultTheme }): string => {
  return `
    .toqio-app-content {
      height: calc(100vh - var(--environment-bar-height));
      overflow: hidden;
      background-color: ${theme.color.general.background};
    }

    .toqio-web-container {
      padding: 0px 16px;
      width: 100%;
      max-width: 1140px;
      @media ${theme.media_queries.mediaS} {
        padding: 0px 24px;
      }
      @media ${theme.media_queries.mediaXXL} {
        width: 100%;
        padding: 0px;
        margin: 0px auto;
      }
    }
    
    .toqio-onboarding-user {
      background-color: ${theme.color.general.background};
    }
  `;
};

const GlobalStyles = createGlobalStyle`
  ${getViewportStyles}
  ${getGeneralStyles}
  ${getContentStyles}
  ${getScrollBarStyles}
  ${getSmallSpacingClasses}
  ${getLargeSpacingClasses}
  ${getFlowableStyles}
`;

export default GlobalStyles;
